import { useTranslation } from 'react-i18next';

import { DefaultWidgetNames, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useDashboardOrganizationSummary } from '@/hooks/useDashboardOrganizationSummary';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';

export const useEcologicalIntegrityIndex = () => {
  const { average_ecological_integrity_score: ecologicalIntegrityIndex } = useDashboardOrganizationSummary().data ?? {};
  const formattedValue = ecologicalIntegrityIndex ? getDisplayNumber(ecologicalIntegrityIndex) : '--';

  const { t } = useTranslation();

  const tooltipStyles = 'typography-caption text-primary';
  const tooltipListItems = [
    {
      text: t('global.widget.ecologicalIntegrityIndex.tooltip.list.item1'),
      style: tooltipStyles,
    },
    {
      text: t('global.widget.ecologicalIntegrityIndex.tooltip.list.item2'),
      style: tooltipStyles,
    },
    {
      text: t('global.widget.ecologicalIntegrityIndex.tooltip.list.item3'),
      style: tooltipStyles,
    },
  ];

  return {
    name: DefaultWidgetNames.default_ecological_integrity_card,
    type: WidgetType.o_card,
    visible: true,
    relatedData: {
      styles: {
        root: 'min-w-[200px] grow lg:basis-1/4 rounded-3xl',
        body: 'flex flex-col gap-4 p-8',
      },
    },
    widgets: [
      {
        name: 'ecological_integrity_header',
        type: WidgetType.a_text,
        visible: true,
        widgets: [
          {
            name: 'ecological_integrity_header_text',
            type: WidgetType.a_text,
            visible: true,
            relatedData: {
              i18nPath: 'global.widget.ecologicalIntegrityIndex.title',
              className: 'typography-body1 text-text-primary',
            },
          },
          {
            name: 'ecological_integrity_header_tooltip',
            type: WidgetType.m_tooltip,
            visible: true,
            relatedData: {},
            widgets: [
              {
                name: 'ecological_integrity_header_tooltip_trigger',
                type: WidgetType.m_tooltip_trigger,
                visible: true,
                widgets: [
                  {
                    name: 'ecological_integrity_header_tooltip_trigger_icon',
                    type: WidgetType.a_icon,
                    visible: true,
                    relatedData: {
                      icon: 'ri-information-line',
                      className: 'ml-1 text-active-54',
                    },
                  },
                ],
              },
              {
                name: 'ecological_integrity_header_tooltip_content',
                type: WidgetType.m_tooltip_content,
                visible: true,
                widgets: [
                  {
                    name: 'ecological_integrity_header_tooltip_text_part1',
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      i18nPath: 'global.widget.ecologicalIntegrityIndex.tooltip.part1',
                    },
                  },
                  {
                    name: 'ecological_integrity_header_tooltip_text_list',
                    type: WidgetType.a_ul_list,
                    visible: true,
                    relatedData: {
                      listItems: tooltipListItems,
                    },
                  },
                  {
                    name: 'ecological_integrity_header_tooltip_text_part2',
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      i18nPath: 'global.widget.ecologicalIntegrityIndex.tooltip.part2',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'ecological_integrity_value',
        type: WidgetType.a_text,
        visible: true,
        relatedData: {
          text: formattedValue,
          className: 'typography-h2 text-text-primary',
        },
      },
    ],
  } as WidgetSlotConfiguration;
};
